import axios, {AxiosError} from 'axios';
import {
  Auction,
  ChatMessage,
  Draft,
  DraftLobby,
  MatchHistoryTeam,
  Pokemon,
  StatsPageResponse,
  UserRole,
} from "../../types";

interface JoinDraftResponse {
  joined: boolean;
  error?: string;
}

interface EeveelutionClaimResponse {
  success: boolean;
  error?: string;
}

// Fetch current user info
export async function fetchCurrentUser(): Promise<{user_id: string | null, username: string | null, avatar?: string, is_guest: boolean, roles?: UserRole[]}> {
  const response = await axios.get('/api/me');
  // Response can be {GuestUser: {...}} or {DiscordUser: {...}}
  const data = response.data;
  if (data.GuestUser) {
    return {
      user_id: data.GuestUser.user_id,
      username: data.GuestUser.user_name,
      is_guest: true,
    };
  } else if (data.DiscordUser) {
    return {
      user_id: data.DiscordUser.user_id ?? data.DiscordUser.id,
      username: data.DiscordUser.user_name ?? data.DiscordUser.username,
      avatar: data.DiscordUser.avatar,
      roles: data.DiscordUser.roles ?? [],
      is_guest: false,
    };
  } else {
    return {
      user_id: null,
      username: null,
      is_guest: false,
      roles: [],
    }
  }
}

// Fetch a draft by id from the backend
export async function fetchDraftById(draft_id: string): Promise<Draft> {
  const response = await axios.get(`/api/drafts/${draft_id}`);
  return response.data;
}

export async function fetchDraftPokemon(draft_id: string): Promise<Pokemon[]> {
  const response = await axios.get(`/api/drafts/${draft_id}/pokemon`);
  return response.data;
}

export async function fetchDraftCurrentAuction(draft_id: string): Promise<Auction> {
  const response = await axios.get(`/api/drafts/${draft_id}/current_auction`);
  return response.data;
}

// Fetch all open drafts for lobby viewer
export async function fetchOpenDrafts(): Promise<DraftLobby[]> {
  const response = await axios.get('/api/drafts');
  return response.data;
}

// Hide a draft from the lobby viewer without deleting it from the database
export async function deleteDraft(draft_id: string): Promise<void> {
  await axios.delete(`/api/drafts/${draft_id}`);
}

// Start a draft
export async function startDraft(draft_id: string): Promise<Draft> {
  await axios.post(`/api/drafts/${draft_id}/start`);
  const response = await axios.get(`/api/drafts/${draft_id}`);
  return response.data;
}

export async function readyUpDraft(draft_id: string): Promise<Draft> {
  await axios.post(`/api/drafts/${draft_id}/ready`, { ready: true });
  const response = await axios.get(`/api/drafts/${draft_id}`);
  return response.data;
}

export async function becomeDraftSpectator(draft_id: string): Promise<Draft> {
  await axios.post(`/api/drafts/${draft_id}/become-spectator`);
  const response = await axios.get(`/api/drafts/${draft_id}`);
  return response.data;
}

export async function pauseDraft(draft_id: string): Promise<void> {
  await axios.post(`/api/drafts/${draft_id}/pause`);
}

export async function unpauseDraft(draft_id: string): Promise<void> {
  await axios.post(`/api/drafts/${draft_id}/unpause`);
}

export async function submitRaceResults(
  draft_id: string,
  placements: Record<string, number>,
): Promise<void> {
  await axios.post(`/api/drafts/${draft_id}/submit-results`, placements);
}

export async function updatePendingDraftSettings(
  draft_id: string,
  num_teams: number,
  num_auctions: number,
  remove_team_ids: string[] = [],
  move_to_spectator_user_ids: string[] = [],
  add_spectator_user_ids: string[] = [],
): Promise<Draft> {
  const response = await axios.post(`/api/drafts/${draft_id}/pending-settings`, {
    num_teams,
    num_auctions,
    remove_team_ids,
    move_to_spectator_user_ids,
    add_spectator_user_ids,
  });

  return response.data;
}

// Join a draft
export async function joinDraft(draft_id: string, password?: string): Promise<Draft> {
  const trimmedPassword = password?.trim();
  let joinResponse;
  try {
    joinResponse = await axios.post(`/api/drafts/${draft_id}/join`, {
      password: trimmedPassword,
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data);
    } else {
      throw error;
    }
  }
   // Fetch and return the updated draft after joining
  const response = await axios.get(`/api/drafts/${draft_id}`);
  return response.data;
}

// Place a bid on the current auction
export async function placeBid(draft_id: string, auction_id: string, value: number): Promise<any> {
  const response = await axios.post(`/api/drafts/${draft_id}/bid`, {
    auction_id,
    value
  });
  return response;
}

// 1v1 draft: pick or ban a pokemon from the shared pool
export async function oneVOnePick(draft_id: string, pokedex_id: number, form: string | null): Promise<void> {
  await axios.post(`/api/drafts/${draft_id}/one-v-one/pick`, { pokedex_id, form });
}

export async function oneVOneBan(draft_id: string, pokedex_id: number, form: string | null): Promise<void> {
  await axios.post(`/api/drafts/${draft_id}/one-v-one/ban`, { pokedex_id, form });
}

export async function oneVOneToggleTimer(draft_id: string): Promise<void> {
  await axios.post(`/api/drafts/${draft_id}/one-v-one/toggle-timer`);
}

export interface AutoBidState {
  enabled: boolean;
  value: number | null;
}

// Fetch the current user's auto-bid setting for a draft
export async function fetchAutoBid(draft_id: string): Promise<AutoBidState> {
  const response = await axios.get(`/api/drafts/${draft_id}/auto-bid`);
  return response.data;
}

// Set the current user's auto-bid setting for a draft
export async function setAutoBid(
  draft_id: string,
  value: number,
  enabled: boolean,
): Promise<AutoBidState> {
  const response = await axios.post(`/api/drafts/${draft_id}/auto-bid`, {
    value,
    enabled,
  });
  return response.data;
}
// Claim an eeveelution after draft completes
export async function claimEeveelution(draft_id: string, pokedex_id: number, form: string | null, target_user_id?: string | null): Promise<any> {
  const response = await axios.post(`/api/drafts/${draft_id}/claim-eeveelution`, {
    pokedex_id,
    form,
    target_user_id
  });
  const data = response.data as EeveelutionClaimResponse;
  if (typeof data?.success === 'boolean' && !data.success) {
    throw new Error(data.error || 'Failed to claim Eeveelution');
  }
  return data;
}

export async function unclaimEeveelution(draft_id: string, pokedex_id: number, form: string | null, target_user_id?: string | null): Promise<any> {
  const response = await axios.post(`/api/drafts/${draft_id}/unclaim-eeveelution`, {
    pokedex_id,
    form,
    target_user_id
  });
  const data = response.data as EeveelutionClaimResponse;
  if (typeof data?.success === 'boolean' && !data.success) {
    throw new Error(data.error || 'Failed to unclaim Eeveelution');
  }
  return response.data;
}

// Record an early forfeit as a loss against the given boss trainer
export async function forfeitDraft(
  draft_id: string,
  trainer_id: number,
  hours: number,
  minutes: number,
  seconds: number,
): Promise<void> {
  await axios.post(`/api/drafts/${draft_id}/forfeit`, {
    trainer_id,
    hours,
    minutes,
    seconds,
  });
}

// Fetch chat messages for a draft
export async function fetchDraftChats(draft_id: string, after?: number): Promise<ChatMessage[]> {
  const params = after !== undefined && after !== null ? `?after=${after}` : '';
  const response = await axios.get(`/api/drafts/${draft_id}/chats${params}`);
  return response.data;
}

// Create a new chat message for a draft
export async function createDraftChat(draft_id: string, message: string): Promise<ChatMessage> {
  try {
    const response = await axios.post(`/api/drafts/${draft_id}/chats`, { message });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data);
    } else {
      throw error;
    }
  }
}

// Change guest name
export async function changeGuestName(newName: string): Promise<string> {
  // Only send the part after 'guest:'
  const trimmed = newName.trim();
  if (!trimmed) throw new Error('Name cannot be empty');
  const response = await axios.post('/api/guests/change-name', { new_name: `guest:${trimmed}` });
  return response.data;
}

// Fetch control bindings for the current user
export async function fetchControlBindings(): Promise<any> {
  const response = await axios.get('/api/control-bindings');
  return response.data;
}

// Save control bindings for the current user
export async function saveControlBindings(controlBindings: any): Promise<any> {
  const response = await axios.post('/api/control-bindings', { control_bindings: controlBindings });
  return response.data;
}

// Restart run and increment attempt count
export async function postRestartRun(
  draft_id: string,
  attempt: number,
): Promise<{ success: boolean; attempt: number }> {
  const response = await axios.post(`/api/drafts/${draft_id}/restart-run`, {
    attempt,
  });
  return response.data;
}
